/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  AndOrExpressionWithValues,
  UserFacingError,
} from '@monetize/types/app';
import { CstNode, CstParser, IToken, Lexer, createToken } from 'chevrotain';
import isNumber from 'lodash/isNumber';

/**
 * Language parser for custom logic expressions
 *
 * Examples of valid inputs and their provided outputs:
 * -----------------------------------------------------------------------------
 * 1 AND 2
 * PARSED TO -->  { AND: [1, 2], values: [1, 2] }
 * -----------------------------------------------------------------------------
 * 1AND2
 * PARSED TO -->  { AND: [1, 2], values: [1, 2] }
 * -----------------------------------------------------------------------------
 * 1 AND (2)
 * PARSED TO -->  { AND: [1, 2], values: [1, 2] }
 * -----------------------------------------------------------------------------
 * (1 AND 2)
 * PARSED TO -->  { AND: [1, 2], values: [1, 2] }
 * -----------------------------------------------------------------------------
 * (1 AND 4)
 * PARSED TO -->  { AND: [1, 4], values: [1, 4] }
 * -----------------------------------------------------------------------------
 * ((((1 AND 2))))
 * PARSED TO -->  { AND: [1, 2], values: [1, 2] }
 * -----------------------------------------------------------------------------
 * (1 AND (2 OR 3))
 * PARSED TO -->  { AND: [1, { OR: [2, 3] }], values: [1, 2, 3] }
 * -----------------------------------------------------------------------------
 * ((1 OR 3) AND (2 OR 3))
 * PARSED TO -->  { AND: [{ OR: [1, 3] }, { OR: [2, 3] }], values: [1, 2, 3] }
 * -----------------------------------------------------------------------------
 * ((1 AND 3) OR (2 AND 3))
 * PARSED TO -->  { OR: [{ AND: [1, 3] }, { AND: [2, 3] }], values: [1, 2, 3] }
 * -----------------------------------------------------------------------------
 * NOT 1
 * PARSED TO -->  { NOT: 1, values: [1] }
 * -----------------------------------------------------------------------------
 * 1 AND NOT 2
 * PARSED TO -->  { AND: [1, { NOT: 2 }], values: [1, 2] }
 * -----------------------------------------------------------------------------
 * 1 AND NOT (2 OR 3)
 * PARSED TO -->  { AND: [1, { NOT: { OR: [2, 3] } }], values: [1, 2, 3] }
 */

type NumberLiteral = string;

interface OrExpression {
  OR: Expression[];
}

interface AndExpression {
  AND: Expression[];
}

interface NotExpression {
  NOT: Expression;
}

type Expression = OrExpression | AndExpression | NotExpression | NumberLiteral;

interface ExpressionContext extends CstNode {
  orExpression: OrExpressionContext[];
}

interface OrExpressionContext extends CstNode {
  andExpression: AndExpressionContext[];
  OrOperator?: IToken[];
}

interface AndExpressionContext extends CstNode {
  atomicExpression: AtomicExpressionContext[];
  AndOperator?: IToken[];
}

interface AtomicExpressionContext extends CstNode {
  NumberLiteral?: IToken[];
  LParen?: IToken[];
  expression?: ExpressionContext[];
  RParen?: IToken[];
  NotOperator?: IToken[];
  atomicExpression?: AtomicExpressionContext[];
}

/**
 * TOKENS
 */

const AndOrOperator = createToken({
  name: 'AndOrOperator',
  pattern: Lexer.NA,
});

const AndOperator = createToken({
  name: 'AndOperator',
  pattern: /AND/i,
});

const OrOperator = createToken({
  name: 'OrOperator',
  pattern: /OR/i,
});

const NotOperator = createToken({
  name: 'NotOperator',
  pattern: /NOT/i,
});

const LParen = createToken({ name: 'LParen', pattern: /\(/ });

const RParen = createToken({ name: 'RParen', pattern: /\)/ });

const NumberLiteral = createToken({
  name: 'NumberLiteral',
  pattern: /[1-9]\d*/,
});

// marking WhiteSpace as 'SKIPPED' makes the lexer skip it.
const WhiteSpace = createToken({
  name: 'WhiteSpace',
  pattern: /\s+/,
  group: Lexer.SKIPPED,
});

const allTokens = [
  WhiteSpace, // whitespace is normally very common so it should be placed first to speed up the lexer's performance
  AndOperator,
  OrOperator,
  NotOperator,
  LParen,
  RParen,
  NumberLiteral,
  AndOrOperator,
];

/**
 * PARSER
 */

class ExpressionParser extends CstParser {
  constructor() {
    super(allTokens);
    this.performSelfAnalysis();
  }

  // Defining the grammar
  public expression = this.RULE('expression', () => {
    this.SUBRULE(this.orExpression);
  });

  private orExpression = this.RULE('orExpression', () => {
    this.SUBRULE(this.andExpression);
    this.MANY(() => {
      this.CONSUME(OrOperator);
      this.SUBRULE2(this.andExpression);
    });
  });

  private andExpression = this.RULE('andExpression', () => {
    this.SUBRULE(this.atomicExpression);
    this.MANY(() => {
      this.CONSUME(AndOperator);
      this.SUBRULE2(this.atomicExpression);
    });
  });

  private atomicExpression = this.RULE('atomicExpression', () => {
    this.OR([
      { ALT: () => this.CONSUME(NumberLiteral) },
      {
        ALT: () => {
          this.CONSUME(NotOperator);
          this.SUBRULE(this.atomicExpression);
        },
      },
      {
        ALT: () => {
          this.CONSUME(LParen);
          this.SUBRULE(this.expression);
          this.CONSUME(RParen);
        },
      },
    ]);
  });
}

const parser = new ExpressionParser();
const lexer = new Lexer(allTokens);

/**
 * VISITOR - INTERPRETER
 */

class ExpressionInterpreter extends parser.getBaseCstVisitorConstructor() {
  constructor() {
    super();
    this.validateVisitor();
  }

  expression(ctx: ExpressionContext, values: Set<number>): Expression {
    return this.visit(ctx.orExpression[0] || [], values);
  }

  orExpression(ctx: OrExpressionContext, values: Set<number>): Expression {
    const andExpressions = ctx.andExpression;
    if (andExpressions.length > 1 || ctx.OrOperator) {
      return {
        OR: andExpressions.map(
          (andExpr) => this.visit(andExpr, values) as Expression,
        ),
      };
    }
    return this.visit(andExpressions[0] || [], values) as Expression;
  }

  andExpression(ctx: AndExpressionContext, values: Set<number>): Expression {
    const atomicExpressions = ctx.atomicExpression;
    if (atomicExpressions.length > 1 || ctx.AndOperator) {
      return {
        AND: atomicExpressions.map(
          (atomicExpr) => this.visit(atomicExpr, values) as Expression,
        ),
      };
    }
    return this.visit(atomicExpressions[0] || [], values) as Expression;
  }

  atomicExpression(
    ctx: AtomicExpressionContext,
    values: Set<number>,
  ): Expression | number {
    if (ctx.NumberLiteral) {
      const value = Number(ctx.NumberLiteral[0]?.image);
      values.add(value);
      return value;
    } else if (ctx.NotOperator) {
      return {
        NOT: this.visit(ctx.atomicExpression?.[0] || [], values) as Expression,
      };
    } else {
      return this.visit(ctx.expression?.[0] || [], values) as Expression;
    }
  }
}

const interpreter = new ExpressionInterpreter();

/**
 * Parse an expression with AND/OR statements
 *
 * @param inputText
 * @returns
 */
export function parseExpression(inputText: string): AndOrExpressionWithValues {
  const lexingResult = lexer.tokenize(inputText);

  if (lexingResult.errors.length > 0) {
    throw new UserFacingError(
      `Lexing errors detected: ${lexingResult.errors?.[0].message}`,
      'INVALID_EXPRESSION',
      lexingResult.errors,
    );
  }

  parser.input = lexingResult.tokens;
  const parsingResult = parser.expression();

  if (parser.errors.length > 0) {
    throw new UserFacingError(
      `Parsing errors detected: ${parser.errors?.[0].message}`,
      'INVALID_EXPRESSION',
      parser.errors,
    );
  }

  try {
    const values: Set<number> = new Set();
    let results = interpreter.visit(parsingResult, values);

    // For inputs like `1`, wrap in AND for data consistency
    if (isNumber(results)) {
      results = { AND: [results] };
    }

    results.values = Array.from(values).sort();

    return results;
  } catch (ex) {
    throw new UserFacingError(
      'Interpretation errors detected',
      'INVALID_EXPRESSION',
      [ex],
    );
  }
}

/**
 * Format an expression with AND/OR statements
 */
export function formatExpression(inputText: string): string {
  parseExpression(inputText); // used to make sure the input is valid
  const lexingResult = lexer.tokenize(inputText);

  return lexingResult.tokens
    .map(({ image }) => image.toUpperCase())
    .map((token) => {
      if (token === 'AND' || token === 'OR' || token === 'NOT') {
        return ` ${token} `;
      }
      return token;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}
